// ============================================================================
// NoticeSection — the reusable Notice widget UI
// ============================================================================
// Renders a materialised notice section (see noticeWidget.ts) as one clean,
// self-contained card: a header with the notice name + an integrated
// "Generate Notice" action, a Notice-Details block, a Recipients service &
// delivery grid, optional Publications / Replies / Rejoinders blocks, and
// Remarks. Reused across every workflow — only the config differs.
//
// It renders the SAME field specs the validation engine sees (DynamicForm
// materialises the spec first), so validation, values and the case_parties
// prefill all keep working unchanged. This component only owns layout + the
// Generate-Notice affordance.

import React, { useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  FieldSpec,
  FormValues,
  NoticeConfig,
  SectionSpec,
} from 'api/types/formSpec.types';
import DynamicField from './DynamicField';
import DynamicGrid from './DynamicGrid';
import NoticeRecipients from './NoticeRecipients';
import type { GridFieldSpec } from 'api/types/formSpec.types';
import type { SeedContext } from './seedResolver';
import NoticeGenerateModal from 'components/modules/litigation-module/components/NoticeGenerateModal';

export interface NoticeSectionProps {
  section: SectionSpec;              // already materialised (has fields[] + notice)
  values: FormValues;
  onFieldChange: (key: string, val: unknown) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  seedContext?: SeedContext;
}

// Field keys the widget lays out in dedicated blocks (everything else is a
// "notice detail" scalar).
const RECIPIENTS_KEY = 'recipients';
const OPTIONAL_GRID_KEYS = ['publications', 'borrower_replies', 'company_replies'];
const REMARKS_KEY = 'remarks';

const OPTIONAL_BLOCK_LABEL: Record<string, string> = {
  publications: 'Newspaper Publications',
  borrower_replies: 'Borrower Replies',
  company_replies: 'Company Rejoinders',
};

// Map notice-detail form values → NoticeMergeService override keys, so the
// generated letter reflects what the user typed. Mirrors the mapping in
// DynamicStateSection (kept local so the widget is self-contained).
function buildOverrides(values: FormValues): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  const put = (from: string, to: string) => {
    const v = values[from];
    if (v != null && v !== '') out[to] = String(v);
  };
  put('notice_no', 'notice_no');
  put('notice_number', 'notice_no');
  put('notice_date', 'notice_date');
  put('cooling_off_days', 'cooling_off_days');
  put('demand_amount', 'overdue_amount');
  put('demand_amount', 'total_overdue');
  put('overdue_amount', 'overdue_amount');
  put('outstanding_amount', 'overdue_amount');
  put('outstanding_amount', 'total_overdue');
  put('amount_demanded', 'overdue_amount');
  put('amount_demanded', 'total_overdue');
  return out;
}

const NoticeSection: React.FC<NoticeSectionProps> = ({
  section, values, onFieldChange, errors, disabled, seedContext,
}) => {
  const cfg: NoticeConfig = section.notice ?? {};
  const caseId = typeof seedContext?.caseId === 'string' ? seedContext.caseId : '';
  const caseCode = typeof seedContext?.caseCode === 'string' ? seedContext.caseCode : '';

  const [noticeOpen, setNoticeOpen] = useState(false);

  // Bucket the materialised fields.
  const { detailFields, recipientsField, optionalGrids, remarksField } = useMemo(() => {
    const detail: FieldSpec[] = [];
    const optional: FieldSpec[] = [];
    let recipients: FieldSpec | null = null;
    let remarks: FieldSpec | null = null;
    for (const f of section.fields ?? []) {
      if (f.key === RECIPIENTS_KEY) recipients = f;
      else if (f.key === REMARKS_KEY) remarks = f;
      else if (OPTIONAL_GRID_KEYS.includes(f.key)) optional.push(f);
      else detail.push(f);
    }
    return { detailFields: detail, recipientsField: recipients, optionalGrids: optional, remarksField: remarks };
  }, [section.fields]);

  const overrides = useMemo(() => buildOverrides(values), [values]);

  const renderField = (f: FieldSpec) =>
    f.key === RECIPIENTS_KEY && f.type === 'grid' ? (
      <NoticeRecipients
        key={f.key}
        field={f as GridFieldSpec}
        value={values[f.key] as never}
        onChange={(next) => onFieldChange(f.key, next)}
        error={errors[f.key]}
        disabled={disabled}
        seedContext={seedContext}
      />
    ) : f.type === 'grid' ? (
      <DynamicGrid
        key={f.key}
        field={f}
        value={values[f.key] as never}
        onChange={(next) => onFieldChange(f.key, next)}
        error={errors[f.key]}
        disabled={disabled}
        seedContext={seedContext}
      />
    ) : (
      <DynamicField
        key={f.key}
        field={f}
        value={values[f.key]}
        onChange={(v) => onFieldChange(f.key, v)}
        error={errors[f.key]}
        disabled={disabled}
        allValues={values}
        seedContext={seedContext}
      />
    );

  return (
    <div style={cardStyle} data-field={section.key}>
      {/* Header */}
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          <IconSystem.mail size={16} style={{ color: '#2563eb', flexShrink: 0 }} />
          <span style={titleStyle}>{cfg.title ?? section.title ?? 'Notice'}</span>
        </div>
        {cfg.notice_code && caseId && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setNoticeOpen(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}
            title="Merge this notice with the account data and print"
          >
            <IconSystem.file size={14} />
            Generate Notice
          </button>
        )}
      </div>

      {cfg.description && (
        <div style={helpStyle}>{cfg.description}</div>
      )}

      <div style={bodyStyle}>
        {/* Notice details */}
        {detailFields.length > 0 && (
          <Block label="Notice Details">
            <div style={detailGridStyle}>{detailFields.map(renderField)}</div>
          </Block>
        )}

        {/* Recipients */}
        {recipientsField && (
          <Block label="Recipients — Service & Delivery"
                 hint="Pre-fill from the loan account, then add / edit / remove recipients. Each row tracks its own dispatch and delivery.">
            {renderField(recipientsField)}
          </Block>
        )}

        {/* Optional blocks — collapsible to keep the form calm */}
        {optionalGrids.map((f) => (
          <CollapsibleBlock
            key={f.key}
            label={OPTIONAL_BLOCK_LABEL[f.key] ?? f.key}
            defaultOpen={Array.isArray(values[f.key]) && (values[f.key] as unknown[]).length > 0}
            count={Array.isArray(values[f.key]) ? (values[f.key] as unknown[]).length : 0}
          >
            {renderField(f)}
          </CollapsibleBlock>
        ))}

        {/* Remarks */}
        {remarksField && (
          <Block label="Remarks">{renderField(remarksField)}</Block>
        )}
      </div>

      {cfg.notice_code && caseId && (
        <NoticeGenerateModal
          isOpen={noticeOpen}
          caseId={caseId}
          caseCode={caseCode}
          templateCode={cfg.notice_code}
          overrides={overrides}
          onClose={() => setNoticeOpen(false)}
        />
      )}
    </div>
  );
};

// ---- Layout helpers ---------------------------------------------------------

const Block: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div style={{ marginBottom: '0.9rem' }}>
    <div style={blockLabelStyle}>{label}</div>
    {hint && <div style={blockHintStyle}>{hint}</div>}
    {children}
  </div>
);

const CollapsibleBlock: React.FC<{
  label: string; count: number; defaultOpen: boolean; children: React.ReactNode;
}> = ({ label, count, defaultOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: '0.75rem', border: '1px solid var(--border-color)', borderRadius: 6 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={collapsibleHeaderStyle}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          <IconSystem.chevronDown
            size={14}
            style={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s', color: 'var(--text-secondary)' }}
          />
          {label}
          {count > 0 && <span style={countPillStyle}>{count}</span>}
        </span>
        {!open && count === 0 && (
          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>optional</span>
        )}
      </button>
      {open && <div style={{ padding: '0.25rem 0.6rem 0.6rem' }}>{children}</div>}
    </div>
  );
};

// ---- Styles -----------------------------------------------------------------

const cardStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: 8,
  background: 'var(--bg-primary)',
  overflow: 'hidden',
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
  padding: '0.6rem 0.85rem',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--border-color)',
};

const titleStyle: React.CSSProperties = {
  fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const helpStyle: React.CSSProperties = {
  padding: '0.5rem 0.85rem 0',
  fontSize: '0.74rem', color: 'var(--text-secondary)',
};

const bodyStyle: React.CSSProperties = { padding: '0.85rem' };

const blockLabelStyle: React.CSSProperties = {
  fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--text-secondary)', marginBottom: '0.4rem',
};

const blockHintStyle: React.CSSProperties = {
  fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', marginTop: '-0.2rem',
};

const detailGridStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.6rem 0.85rem',
};

const collapsibleHeaderStyle: React.CSSProperties = {
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0.5rem 0.6rem', border: 'none', background: 'transparent', cursor: 'pointer',
  fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)',
};

const countPillStyle: React.CSSProperties = {
  fontSize: '0.68rem', fontWeight: 600, color: '#2563eb',
  border: '1px solid #2563eb', borderRadius: 999, padding: '0 0.4rem',
};

export default NoticeSection;
